import { HttpErrorResponse, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import type { BeginRoomFileUploadResponse, RoomFileErrorCode } from '@andor/contracts/files';

import { FilesService as FilesApi } from '../../shared/api/generated/files/files.service';
import { ANDOR_BASE_URL } from '../../shared/api/generated/api.base-url';
import { backendRequestHeaders } from '../../core/interceptors/default-headers.interceptor';
import { SessionService } from '../../core/services/session.service';
import { ApiCompatibilityService } from '../../core/services/api-compatibility.service';
import { ChatService } from '../../core/services/chat.service';
import { OdinService } from '../../core/services/odin.service';
import { RealtimeService } from '../../core/services/realtime.service';
import { UserService } from '../../core/services/user.service';
import { AppConfigStore } from '../../state/app-config.repository';
import { PermissionStore } from '../../state/permissions.repository';
import { RoomPasswordStore } from '../../state/room-passwords.repository';
import { UserSettingsStore } from '../../state/user-settings.repository';
import { User, UserStore } from '../../state/user.repository';
import type { MessageTransfer } from '../../state/messages.repository';
import { downloadFile } from '../../shared/helpers/download-file';
import { type DiskTarget, pickDiskTarget } from '../../shared/helpers/save-file';
import {
  createDecryptStream,
  createFileCipher,
  decryptFile,
  encryptFileChunk,
  type FileCipher,
  unlockFile,
  WrongFilePasswordError,
} from '../../shared/helpers/file-crypto';
import { formatBytes } from '../../shared/helpers/upload-image';
import {
  RoomFile,
  RoomFileDownloadResult,
  RoomFileDraft,
  RoomFileUpload,
  RoomFileUsage,
} from './room-file';

/** Capability advertised by servers that implement room file sharing. */
export const ROOM_FILES_CAPABILITY = 'room-files';

const DEFAULT_DRAFT: Omit<RoomFileDraft, 'files' | 'protection'> = {
  retention: '1d',
  password: '',
};

/**
 * Files shared in the room the user is in. The server owns the list: it is
 * loaded on join and on websocket reconnects, and kept current by the
 * `room-file-added` / `room-files-removed` events, which reach only members of
 * the room. Uploads run here (not in the menu component) so they survive the
 * menu closing: optional client-side encryption, then the file in fixed-size
 * parts, then a file card in chat for the room.
 */
@Injectable({ providedIn: 'root' })
export class RoomFilesService {
  private readonly _api = inject(FilesApi);
  private readonly _compat = inject(ApiCompatibilityService);
  private readonly _chat = inject(ChatService);
  private readonly _odin = inject(OdinService);
  private readonly _realtime = inject(RealtimeService);
  private readonly _users = inject(UserService);
  private readonly _userStore = inject(UserStore);
  private readonly _config = inject(AppConfigStore);
  private readonly _roomPasswords = inject(RoomPasswordStore);
  private readonly _translate = inject(TranslateService);
  private readonly _session = inject(SessionService);
  private readonly _userSettings = inject(UserSettingsStore);
  private readonly _baseUrl = inject(ANDOR_BASE_URL);

  private readonly _files = signal<RoomFile[]>([]);
  private readonly _usedBytes = signal<number | null>(null);
  /** The list of the joined room has been loaded at least once. */
  private readonly _loaded = signal(false);
  private readonly _uploads = signal<RoomFileUpload[]>([]);
  private readonly _draft = signal<RoomFileDraft | null>(null);
  private readonly _downloading = signal<ReadonlySet<string>>(new Set());
  /** Running or queued uploads, by upload id; aborted on cancel. */
  private readonly _aborts = new Map<string, AbortController>();
  private _queue: Promise<void> = Promise.resolve();
  private _loadSeq = 0;

  /** The room whose files are shown: the joined room. */
  readonly roomId = this._odin.joinedRoomId;
  readonly limits = this._config.fileSharing;
  /** The server supports file sharing and it is turned on for this app. */
  readonly available = computed(
    () => this._compat.hasCapability(ROOM_FILES_CAPABILITY) && this.limits().maxTotalBytes > 0
  );
  readonly canShare = toSignal(inject(PermissionStore).shareFiles$, { initialValue: false });
  /** Newest first. */
  readonly files = computed(() => [...this._files()].sort((a, b) => b.uploadedAt - a.uploadedAt));
  readonly uploads = this._uploads.asReadonly();
  readonly loaded = this._loaded.asReadonly();
  readonly draft = this._draft.asReadonly();
  readonly downloading = this._downloading.asReadonly();
  readonly usage = computed<RoomFileUsage | null>(() => {
    const used = this._usedBytes();
    return used === null ? null : { usedBytes: used, ...this.limits() };
  });
  /** The joined room has a password: its files can be encrypted with it. */
  readonly roomHasPassword = computed(() => {
    const roomId = this.roomId();
    return !!roomId && this._roomPasswords.has(roomId);
  });
  /** Number of shared files, for the paperclip badge. */
  readonly count = computed(() => this._files().length);

  constructor() {
    let shownRoomId: string | null = null;
    effect(() => {
      const roomId = this.roomId();
      const available = this.available();
      untracked(() => {
        // Another room (or none): nothing of the previous one carries over.
        if (roomId !== shownRoomId) {
          shownRoomId = roomId;
          this._files.set([]);
          this._usedBytes.set(null);
          this._loaded.set(false);
          this._draft.set(null);
        }
        if (roomId && available) void this.refresh();
      });
    });
    this._realtime.onReconnect$.subscribe(() => void this.refresh());
    this._realtime.serverEvents$
      .pipe(
        filter((event) => event.type === 'room-file-added' || event.type === 'room-files-removed')
      )
      .subscribe((event) => {
        if (event.type === 'room-file-added') {
          if (event.data.roomId !== this.roomId()) return;
          this._upsert(event.data.file);
          this._usedBytes.set(event.data.usedBytes);
        } else if (event.type === 'room-files-removed') {
          if (event.data.roomId !== this.roomId()) return;
          const removed = new Set(event.data.fileIds);
          this._files.update((files) => files.filter((file) => !removed.has(file.id)));
          if (event.data.usedBytes !== undefined) this._usedBytes.set(event.data.usedBytes);
        }
      });
  }

  /** The listed file with this id, if it still exists. */
  find(fileId: string): RoomFile | undefined {
    return this._files().find((file) => file.id === fileId);
  }

  isOwn(file: RoomFile): boolean {
    return file.uploadedBy === this._users.ownUser()?.id;
  }

  /** Reload the list (menu opened, reconnect, join). */
  async refresh(): Promise<void> {
    const roomId = this.roomId();
    if (!roomId || !this.available()) return;
    const seq = ++this._loadSeq;
    try {
      const { files, usage } = await firstValueFrom(this._api.listRoomFiles({ roomId }));
      if (seq !== this._loadSeq || roomId !== this.roomId()) return;
      this._files.set(files);
      this._usedBytes.set(usage.usedBytes);
      this._loaded.set(true);
    } catch (e) {
      // Not admitted (yet) or a transient failure: keep what we have; the
      // next event, reconnect or menu opening retries.
      console.warn('Could not load the room files', e);
    }
  }

  // -- Draft ---------------------------------------------------------------

  /** Files picked in the menu: open the upload panel for them. */
  select(files: File[]) {
    if (!files.length || !this.canShare()) return;
    const current = this._draft();
    this._draft.set({
      ...DEFAULT_DRAFT,
      protection: this.roomHasPassword() ? 'room' : 'none',
      ...current,
      files: [...(current?.files ?? []), ...files],
    });
  }

  updateDraft(patch: Partial<Omit<RoomFileDraft, 'files'>>) {
    this._draft.update((draft) => (draft ? { ...draft, ...patch } : draft));
  }

  removeFromDraft(file: File) {
    this._draft.update((draft) => {
      if (!draft) return draft;
      const files = draft.files.filter((f) => f !== file);
      return files.length ? { ...draft, files } : null;
    });
  }

  discardDraft() {
    this._draft.set(null);
  }

  /** Why a drafted file cannot be uploaded, if it cannot (shown before sending). */
  draftProblem(file: File): string | null {
    const { maxFileBytes } = this.limits();
    if (file.size === 0) return this._translate.instant('Empty files cannot be shared.');
    if (file.size > maxFileBytes) {
      return this._translate.instant('Larger than the maximum of [max].', {
        max: formatBytes(maxFileBytes, 0),
      });
    }
    return null;
  }

  /** Upload every valid drafted file with the draft's options, one at a time. */
  uploadDraft() {
    const draft = this._draft();
    const roomId = this.roomId();
    if (!draft || !roomId) return;
    const password =
      draft.protection === 'room'
        ? (this._roomPasswords.get(roomId) ?? '')
        : draft.protection === 'custom'
          ? draft.password
          : '';
    if (draft.protection !== 'none' && !password) return;
    this._draft.set(null);
    for (const file of draft.files) {
      if (this.draftProblem(file)) continue;
      const upload: RoomFileUpload = {
        id: crypto.randomUUID(),
        name: file.name,
        size: file.size,
        mime: file.type,
        encrypted: draft.protection !== 'none',
        state: 'preparing',
        progress: 0,
      };
      this._uploads.update((uploads) => [...uploads, upload]);
      const abort = new AbortController();
      this._aborts.set(upload.id, abort);
      this._queue = this._queue.then(() =>
        this._upload(upload.id, file, roomId, draft, password, abort.signal)
          .catch((e) => this._fail(upload.id, e, abort.signal))
          .finally(() => this._aborts.delete(upload.id))
      );
    }
  }

  /** Stop an upload (the server discards what it already received). */
  cancelUpload(id: string) {
    this._aborts.get(id)?.abort();
    this._uploads.update((uploads) => uploads.filter((u) => u.id !== id));
  }

  dismissUpload(id: string) {
    this._uploads.update((uploads) => uploads.filter((u) => u.id !== id));
  }

  // -- Upload --------------------------------------------------------------

  private async _upload(
    id: string,
    file: File,
    roomId: string,
    draft: RoomFileDraft,
    password: string,
    signal: AbortSignal
  ): Promise<void> {
    if (signal.aborted) return;
    const cipher: FileCipher | null = password
      ? await createFileCipher(password, draft.protection === 'room' ? 'room' : 'custom')
      : null;
    if (signal.aborted) return;
    const started: BeginRoomFileUploadResponse = await firstValueFrom(
      this._api.beginRoomFileUpload({
        roomId,
        name: file.name,
        mime: file.type,
        size: file.size,
        retention: draft.retention,
        uploaderName: this._users.ownUser()?.data.name ?? '',
        ...(cipher ? { encryption: cipher.encryption } : {}),
      })
    );
    const { uploadId, partBytes, partCount, storedSize } = started;
    let completed: { file: RoomFile; usedBytes: number };
    try {
      this._patch(id, { state: 'uploading' });
      for (let partNumber = 1; partNumber <= partCount; partNumber++) {
        signal.throwIfAborted();
        const part = cipher
          ? new Blob([await encryptFileChunk(file, cipher, partNumber - 1)])
          : file.slice((partNumber - 1) * partBytes, partNumber * partBytes);
        const before = (partNumber - 1) * partBytes;
        await this._sendPart(id, uploadId, partNumber, part, signal, (loaded) =>
          this._patch(id, { progress: Math.min(1, (before + loaded) / storedSize) })
        );
      }
      signal.throwIfAborted();
      this._patch(id, { state: 'completing', progress: 1 });
      const { file: shared, usage } = await firstValueFrom(
        this._api.completeRoomFileUpload(uploadId)
      );
      completed = { file: shared, usedBytes: usage.usedBytes };
    } catch (e) {
      // Release the reservation right away instead of waiting for expiry.
      void firstValueFrom(this._api.abortRoomFileUpload(uploadId)).catch(() => undefined);
      throw e;
    }
    this._uploads.update((uploads) => uploads.filter((u) => u.id !== id));
    // The user may have moved on to another room meanwhile: the card belongs
    // to the room the file was shared in only.
    if (roomId === this.roomId()) {
      this._upsert(completed.file);
      this._usedBytes.set(completed.usedBytes);
      await this._announce(completed.file);
    }
  }

  /**
   * One part, with upload progress. Rate limits (429) and busy upload slots
   * (503) are waited out as long as the server asks (`Retry-After`), a few
   * times; network errors and other server failures are retried once.
   */
  private async _sendPart(
    id: string,
    uploadId: string,
    partNumber: number,
    body: Blob,
    signal: AbortSignal,
    onProgress: (loaded: number) => void
  ): Promise<void> {
    const part = new Blob([body], { type: 'application/octet-stream' });
    for (let attempt = 1; ; attempt++) {
      try {
        await lastResponse(
          this._api.uploadRoomFilePart(uploadId, partNumber, part, {
            observe: 'events',
            reportProgress: true,
          }),
          signal,
          onProgress
        );
        return;
      } catch (e) {
        if (signal.aborted || !(e instanceof HttpErrorResponse)) throw e;
        const throttled = e.status === 429 || e.status === 503;
        const transient = e.status === 0 || e.status >= 500;
        const retryable = throttled ? attempt < MAX_THROTTLED_ATTEMPTS : transient && attempt < 2;
        if (!retryable) throw e;
        this._patch(id, { state: 'waiting' });
        await sleep(Math.min(retryAfterSeconds(e), MAX_RETRY_WAIT_SECONDS) * 1000, signal);
        this._patch(id, { state: 'uploading' });
      }
    }
  }

  /** The file card in chat, to the sender's current context like a text message. */
  private async _announce(file: RoomFile): Promise<void> {
    const ownUser = this._users.ownUser();
    if (!ownUser) return;
    const transfer: MessageTransfer = {
      id: crypto.randomUUID(),
      scope: 'room',
      kind: 'file',
      payload: {
        fileId: file.id,
        name: file.name,
        size: file.size,
        mime: file.mime,
        encrypted: !!file.encryption,
      },
    };
    const breakout = ownUser.data.breakoutRoomId ?? '';
    if (breakout === '') {
      await this._odin.sendMessage(transfer, []);
      return;
    }
    const peers = (
      Object.values(this._userStore.store.getValue().entities) as (User | undefined)[]
    ).filter((u): u is User => !!u && !u.isSelf && u.data.breakoutRoomId === breakout);
    if (peers.length === 0) await this._chat.buildMessage(ownUser, transfer);
    else await this._odin.sendMessage(transfer, peers);
  }

  private _fail(id: string, error: unknown, signal: AbortSignal) {
    if (signal.aborted) return;
    console.warn('Room file upload failed', error);
    this._patch(id, { state: 'failed', error: this._errorText(error) });
  }

  private _errorText(error: unknown): string {
    const code: RoomFileErrorCode | undefined =
      error instanceof HttpErrorResponse ? error.error?.code : undefined;
    const { maxFileBytes, maxTotalBytes } = this.limits();
    switch (code) {
      case 'FILE_TOO_LARGE':
        return this._translate.instant('Larger than the maximum of [max].', {
          max: formatBytes(maxFileBytes, 0),
        });
      case 'STORAGE_QUOTA_EXCEEDED': {
        const free = Math.max(0, maxTotalBytes - (this._usedBytes() ?? 0));
        return this._translate.instant('Not enough storage left ([free] of [total] free).', {
          free: formatBytes(free, 1),
          total: formatBytes(maxTotalBytes, 0),
        });
      }
      case 'TOO_MANY_UPLOADS':
        return this._translate.instant('Too many uploads at once. Try again shortly.');
      case 'FILE_SHARING_DISABLED':
        return this._translate.instant('File sharing is turned off for this app.');
      case 'NOT_IN_ROOM':
      case 'ROOM_NOT_FOUND':
        return this._translate.instant('You are no longer in this room.');
      default:
        return this._translate.instant('Upload failed.');
    }
  }

  // -- Download ------------------------------------------------------------

  /**
   * Download a file. Encrypted files are decrypted in the browser: with the
   * given password, else with the room password for files encrypted with it.
   * The password is checked first, so a wrong one never opens a save dialog.
   * Where the browser offers a save dialog (Chromium), the file streams
   * straight to disk — decrypted chunk by chunk — and never sits in memory;
   * elsewhere it is collected in a Blob and handed to the browser's download.
   */
  async download(file: RoomFile, password?: string): Promise<RoomFileDownloadResult> {
    const roomId = this.roomId();
    if (!roomId || this._downloading().has(file.id)) return 'failed';
    this._setDownloading(file.id, true);
    try {
      let key: CryptoKey | null = null;
      if (file.encryption) {
        const candidate =
          password ??
          (file.encryption.keySource === 'room' ? this._roomPasswords.get(roomId) : undefined);
        if (!candidate) return 'password-required';
        try {
          key = await unlockFile(candidate, file.encryption);
        } catch (e) {
          if (!(e instanceof WrongFilePasswordError)) throw e;
          return password === undefined ? 'password-required' : 'wrong-password';
        }
      }
      // Still within the click's user-gesture window: deriving the key takes
      // well under a second on desktop. A refused dialog falls back below.
      const target = await pickDiskTarget(file.name);
      if (target === 'cancelled') return 'cancelled';
      if (target) {
        try {
          await this._streamToDisk(file, roomId, key, target);
        } catch (e) {
          await target.discard();
          throw e;
        }
      } else {
        const stored = await firstValueFrom(this._api.downloadRoomFile(file.id, { roomId }));
        const content =
          key && file.encryption ? await decryptFile(stored, key, file.encryption) : stored;
        downloadFile(content, file.name);
      }
      return 'downloaded';
    } catch (e) {
      const status = e instanceof HttpErrorResponse || e instanceof DownloadError ? e.status : 0;
      if (status === 404) {
        this._files.update((files) => files.filter((f) => f.id !== file.id));
        return 'gone';
      }
      console.warn('Room file download failed', e);
      return 'failed';
    } finally {
      this._setDownloading(file.id, false);
    }
  }

  /**
   * Stream the stored bytes into `target`, decrypting on the way. A plain
   * `fetch` (the generated client cannot hand out the response as a stream)
   * with the credentials `HttpClient` requests carry.
   */
  private async _streamToDisk(
    file: RoomFile,
    roomId: string,
    key: CryptoKey | null,
    target: DiskTarget
  ): Promise<void> {
    const url =
      `${this._baseUrl}/v1/files/${encodeURIComponent(file.id)}/content` +
      `?roomId=${encodeURIComponent(roomId)}`;
    const response = await fetch(url, {
      headers: backendRequestHeaders(
        this._session.sessionToken(),
        this._userSettings.value.userName
      ),
      credentials: 'include',
    });
    if (!response.ok || !response.body) {
      void response.body?.cancel();
      throw new DownloadError(response.status);
    }
    // The server may re-issue the session token on any response.
    const renewed = response.headers.get('x-andor-uuid');
    if (renewed) this._session.updateFromHeader(renewed);
    const content =
      key && file.encryption
        ? response.body.pipeThrough(createDecryptStream(key, file.encryption))
        : response.body;
    // Closes (commits) the file on success; aborts the write on any error.
    await content.pipeTo(target.writable);
  }

  private _setDownloading(fileId: string, active: boolean) {
    this._downloading.update((ids) => {
      const next = new Set(ids);
      if (active) next.add(fileId);
      else next.delete(fileId);
      return next;
    });
  }

  /** Delete any file of the room (needs `shareFiles`). */
  async remove(file: RoomFile): Promise<boolean> {
    const roomId = this.roomId();
    if (!roomId) return false;
    try {
      await firstValueFrom(this._api.deleteRoomFile({ roomId, fileId: file.id }));
      this._files.update((files) => files.filter((f) => f.id !== file.id));
      return true;
    } catch (e) {
      if (e instanceof HttpErrorResponse && e.status === 404) {
        this._files.update((files) => files.filter((f) => f.id !== file.id));
        return true;
      }
      console.warn('Room file deletion failed', e);
      return false;
    }
  }

  private _upsert(file: RoomFile) {
    this._files.update((files) => [...files.filter((f) => f.id !== file.id), file]);
  }

  private _patch(id: string, patch: Partial<RoomFileUpload>) {
    this._uploads.update((uploads) => uploads.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  }
}

/** Attempts per part while the server answers 429/503 (rate limit, busy slots). */
const MAX_THROTTLED_ATTEMPTS = 8;
/** Longest single wait for `Retry-After`, in seconds. */
const MAX_RETRY_WAIT_SECONDS = 30;

/** The server's `Retry-After` (header or JSON body), else 2 seconds. */
function retryAfterSeconds(error: HttpErrorResponse): number {
  for (const value of [error.headers?.get('Retry-After'), error.error?.retryAfter]) {
    const seconds = Number(value);
    if (value !== null && value !== undefined && value !== '' && seconds >= 0) return seconds;
  }
  return 2;
}

/** Resolves after `ms`; rejects at once when `signal` aborts (upload cancelled). */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(signal.reason);
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal.reason);
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/** A streamed download the server answered with an error status. */
class DownloadError extends Error {
  constructor(readonly status: number) {
    super(`Download failed with status ${status}`);
    this.name = 'DownloadError';
  }
}

/**
 * Resolve with the final response of an event stream, reporting upload
 * progress. Aborting `signal` cancels the request.
 */
function lastResponse<T>(
  events: Observable<HttpEvent<T>>,
  signal: AbortSignal,
  onProgress: (loaded: number) => void
): Promise<HttpResponse<T>> {
  return new Promise((resolve, reject) => {
    let response: HttpResponse<T> | undefined;
    const cancel = () => {
      subscription.unsubscribe();
      reject(signal.reason);
    };
    const subscription = events.subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) onProgress(event.loaded);
        else if (event.type === HttpEventType.Response) response = event;
      },
      error: (e) => {
        signal.removeEventListener('abort', cancel);
        reject(e);
      },
      complete: () => {
        signal.removeEventListener('abort', cancel);
        if (response) resolve(response);
        else reject(new Error('No response'));
      },
    });
    if (signal.aborted) cancel();
    else signal.addEventListener('abort', cancel, { once: true });
  });
}
